// ---------------------------------------------------------------------------
// PREVIEW DA COMANDA — a bobina, na tela.
//
// Este componente NÃO desenha comanda: ele só materializa em HTML as linhas
// que o renderizador compartilhado devolve (src/comum/layout-comanda). É o
// MESMO renderizador que o alfredo-ops-backend usa para falar com a impressora
// térmica — por isso o preview não pode mentir sobre o que vai sair no papel.
//
// A trava que garante isso é o hash em src/comum/layout-comanda/ASSINATURA.json,
// conferido no prebuild deste repositório (ferramentas/verificarLayoutComanda.js).
// ---------------------------------------------------------------------------

import React, { useMemo } from "react";
import {
  LayoutComanda,
  LinhaComanda,
  PedidoParaImpressao,
  renderizarComanda,
} from "../../../../comum/layout-comanda";

/**
 * Colunas PADRÃO por largura de papel — o valor que o layout assume quando o
 * usuário simula o mesmo desenho em outro papel. NÃO é limite: ver
 * MAXIMO_COLUNAS_POR_PAPEL logo abaixo.
 */
export const COLUNAS_POR_PAPEL: Record<80 | 58, number> = {
  80: 48,
  58: 32,
};

/**
 * Quantas colunas a impressora térmica REALMENTE aceita em cada papel.
 *
 * 80mm = 56: provado em alfredo-ops-backend/src/services/printer.service.ts —
 * `printProductOrderProduction` (a via de produção que a cozinha imprime hoje)
 * abre o ThermalPrinter com `width: 56`. As demais vias usam 48 por escolha de
 * desenho, não por limite do papel.
 *
 * 58mm = 32: PREMISSA. Nenhuma impressora é aberta com esse papel no OPS, então
 * não há prova no código; 32 é a largura de fonte A em 58mm que o layout padrão
 * já usava. Se aparecer o número real, é aqui que ele entra.
 *
 * Comparar contra COLUNAS_POR_PAPEL era o defeito: 56 colunas em 80mm é o
 * padrão de PRODUÇÃO, então a tarja laranja acendia na carga, sempre, para
 * todo mundo — e um alerta que está sempre aceso não alerta ninguém.
 */
const MAXIMO_COLUNAS_POR_PAPEL: Record<80 | 58, number> = {
  80: 56,
  58: 32,
};

interface PreviewComandaProps {
  layout: LayoutComanda;
  pedido: PedidoParaImpressao;
  /** Papel escolhido no editor. Quando omitido, usa o do próprio layout. */
  larguraPapelMm?: 80 | 58;
}

/** Altura de uma linha em "ch" — casa com o passo do papel térmico. */
const ALTURA_LINHA = 1.32;

function estiloDaLinha(
  linha: LinhaComanda
): { linha: React.CSSProperties; texto: React.CSSProperties } {
  const alinhamento =
    linha.alinhamento === "centro"
      ? "center"
      : linha.alinhamento === "direita"
      ? "right"
      : "left";

  const escalaX = linha.larguraDupla ? 2 : 1;
  const escalaY = linha.alturaDupla ? 2 : 1;

  return {
    linha: {
      textAlign: alinhamento,
      // Altura dupla ocupa duas linhas de papel — o preview tem que gastar o
      // mesmo espaço, senão a comanda "encolhe" na tela.
      height: `${ALTURA_LINHA * escalaY}em`,
      lineHeight: `${ALTURA_LINHA * escalaY}em`,
      whiteSpace: "pre",
      overflow: "visible",
    },
    texto: {
      display: "inline-block",
      transform: `scale(${escalaX}, ${escalaY})`,
      transformOrigin:
        linha.alinhamento === "centro"
          ? "center center"
          : linha.alinhamento === "direita"
          ? "right center"
          : "left center",
      fontWeight: linha.negrito ? 700 : 400,
      whiteSpace: "pre",
    },
  };
}

const PreviewComanda: React.FC<PreviewComandaProps> = ({
  layout,
  pedido,
  larguraPapelMm,
}) => {
  const papel = larguraPapelMm ?? layout.larguraPapelMm;

  /**
   * Colunas PEDIDAS no papel escolhido — o que o layout quer imprimir.
   * Simulando OUTRO papel (o comparativo 80mm x 58mm), o layout assume o padrão
   * daquele papel; no papel do próprio layout, vale o valor editado.
   */
  const colunasPedidas =
    papel === layout.larguraPapelMm
      ? layout.larguraColunas
      : COLUNAS_POR_PAPEL[papel];

  // Trocar de papel muda a largura em CARACTERES — é o que faz a quebra de
  // linha mudar de lugar. E a impressora não imprime mais colunas do que o
  // papel aceita: o preview desenha no que REALMENTE sai, senão a tarja abaixo
  // prometeria uma quebra que a tela não mostra. (Era o que acontecia depois
  // que o papel passou a morar no layout: `papel` e `layout.larguraPapelMm`
  // viraram sempre iguais, o recorte nunca rodava, e escolher 58mm com 48
  // colunas continuava desenhando 48 colunas.)
  const layoutNoPapel = useMemo<LayoutComanda>(() => {
    const colunasQueSaem = Math.min(
      colunasPedidas,
      MAXIMO_COLUNAS_POR_PAPEL[papel]
    );
    if (
      papel === layout.larguraPapelMm &&
      colunasQueSaem === layout.larguraColunas
    ) {
      return layout;
    }
    return {
      ...layout,
      larguraColunas: colunasQueSaem,
      larguraPapelMm: papel,
    };
  }, [layout, papel, colunasPedidas]);

  const linhas = useMemo(
    () => renderizarComanda(layoutNoPapel, pedido),
    [layoutNoPapel, pedido]
  );

  /** O que o preview está desenhando — já recortado ao que a impressora aceita. */
  const colunas = layoutNoPapel.larguraColunas;
  const naoCabeNoPapel = colunasPedidas > MAXIMO_COLUNAS_POR_PAPEL[papel];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {naoCabeNoPapel && (
        <div
          style={{
            background: "#fff4e5",
            border: "1px solid #ffb74d",
            borderRadius: 6,
            padding: "var(--sp-8) var(--sp-12)",
            fontSize: 12,
            color: "#7a4a00",
          }}
        >
          Este layout usa {colunasPedidas} colunas, mas em {papel}mm a
          impressora aceita no máximo {MAXIMO_COLUNAS_POR_PAPEL[papel]}. O
          preview abaixo já está nas {colunas} colunas que vão sair de verdade.
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          background: "#eceff1",
          padding: "var(--sp-24) var(--sp-12)",
          borderRadius: 10,
          overflowX: "auto",
        }}
      >
        <div
          // A "bobina": papel branco, borda serrilhada em cima e embaixo.
          style={{
            background: "#ffffff",
            boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
            // Fixo de propósito, fora da escala fluida: esta caixa é a
            // SIMULAÇÃO DO PAPEL, e o conteúdo dela é medido em caracteres
            // (`${colunas}ch` com fonte fixa de 12px) porque é assim que a
            // impressora térmica mede. Se a margem do papel encolhesse com a
            // janela e o conteúdo não, o preview deixaria de ser fiel ao que
            // sai impresso — e é justamente para isso que ele existe.
            padding: "18px 10px",
            position: "relative",
          }}
        >
          <BordaSerrilhada posicao="topo" />
          <pre
            style={{
              margin: 0,
              fontFamily:
                "'Roboto Mono','DejaVu Sans Mono','Courier New',monospace",
              fontSize: 12,
              color: "#1a1a1a",
              // A largura é medida em caracteres: é assim que o papel é medido.
              width: `${colunas}ch`,
              whiteSpace: "pre",
            }}
          >
            {linhas.map((linha, indice) => {
              const estilos = estiloDaLinha(linha);
              return (
                <div key={indice} style={estilos.linha}>
                  <span style={estilos.texto}>
                    {linha.texto === "" ? " " : linha.texto}
                  </span>
                </div>
              );
            })}
          </pre>
          <BordaSerrilhada posicao="rodape" />
        </div>
      </div>

      <div style={{ fontSize: 11, color: "#607d8b", textAlign: "center" }}>
        {papel}mm · {colunas} colunas · {linhas.length} linhas ·{" "}
        {layoutNoPapel.documento === "producao"
          ? "via de produção"
          : "via de conferência"}
      </div>
    </div>
  );
};

const BordaSerrilhada: React.FC<{ posicao: "topo" | "rodape" }> = ({
  posicao,
}) => (
  <div
    style={{
      position: "absolute",
      left: 0,
      right: 0,
      [posicao === "topo" ? "top" : "bottom"]: -6,
      height: 6,
      background:
        "linear-gradient(-45deg, transparent 33.333%, #ffffff 33.333%, #ffffff 66.667%, transparent 66.667%)," +
        "linear-gradient(45deg, transparent 33.333%, #ffffff 33.333%, #ffffff 66.667%, transparent 66.667%)",
      backgroundSize: "8px 12px",
    } as React.CSSProperties}
  />
);

export default PreviewComanda;
